/**
 * Packages sub-tab (v2 — DNKADM-0102, DNKAPI-0181) — the Packages list surface.
 *
 * A **Package** is an `offerings` row with `kind=package`: a one-time-purchase
 * offering that grants credits once (name · price + an explicit by-bucket
 * `credits` array — "what's in it"). This tab lists them (mirroring {@link PlansTab})
 * with the shared **Active / Inactive / All** filter (`useActiveFilter` +
 * `<ActiveFilter>`, §4.3; deactivated rows dimmed under All) and the row actions that
 * don't need the create/edit dialog. The shown status chip reflects the lifecycle
 * **`status`** (DNKAPI-0261: Draft / Active / Inactive), NOT the `active` flag; the
 * per-row actions are keyed on that `status`: **Publish** (a Draft goes live),
 * **Deactivate/Reactivate** (flip `active` on a published row, the row stays),
 * **Copy** (offered on every row — one-click deep-clone into a new Draft) and
 * **Delete** (confirm — a soft-remove; the row is gone from `status=all` afterward).
 * Row dimming stays keyed on `active` (a Draft is `active:true` ⇒ not dimmed). Money
 * renders via the 0049 helpers (`formatMoney` with the tenant currency from
 * `useTenantCurrency`). A package restricted to members or to the front desk carries a
 * small "Members only" / "Front desk only" chip by its name (DNKADM-0412).
 *
 * **Deferred to U2** (not built here): the "Add package" CTA, the Edit row action,
 * `PackageDialog`, and the by-bucket credit-grant editor. The Add button renders
 * disabled as a coherent placeholder until U2 wires the dialog.
 *
 * **Toolbar, sort and URL (DNKADM-0430 P1-F).** The shared {@link ListToolbar} holds a
 * package name search and the Active/Inactive/All filter; Package, Price, Contents and
 * Status have shared {@link SortableHeader}s (default Package name ascending). Search,
 * filter and sort live in the URL (`packagesListView.ts`).
 *
 * No "GoHighLevel"/"GHL" reaches any rendered string (white-label §4.0).
 */
import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { ListNoMatch } from '@/components/list/ListNoMatch';
import { ListToolbar } from '@/components/list/ListToolbar';
import { SortableHeader } from '@/components/list/SortableHeader';
import { parseSort, sortRows, toggleSort, toSortParams } from '@/components/list/listSort';
import { useListUrlState } from '@/components/list/useListUrlState';
import { Icon, resolveTokens } from '@/theme';
import { useResource } from '@/hooks/useResource';
import { useUrlActiveFilter } from '@/hooks/useActiveFilter';
import { ActiveFilter } from '@/components/ActiveFilter';
import { useToast } from '@/components/useToast';
import { useTenantCurrency } from '@/hooks/useTenantCurrency';
import { serverMessage } from '@/api/serverMessage';
import { formatMoney } from '@/utils/v2Format';
import {
  copyOffering,
  deactivateOffering,
  deleteOffering,
  listPackages,
  publishOffering,
  reactivateOffering,
  type Offering,
  type PurchaseAudience,
} from '@/api/offerings';
import { PackageDialog } from './PackageDialog';
import {
  PACKAGE_DEFAULT_SORT,
  PACKAGE_SORT_KEYS,
  PACKAGES_URL_DEFAULTS,
  packageContentsLabel,
  packageMatches,
  packageName,
  packageSortValue,
  packageStatusLabel,
  type PackageSortKey,
} from './packagesListView';
import { ScreenError } from '@/components/ScreenError';

/** The row chip for a restricted audience (DNKADM-0412); `everyone` shows no chip. */
const AUDIENCE_CHIP: Record<Exclude<PurchaseAudience, 'everyone'>, string> = {
  members: 'Members only',
  staff: 'Front desk only',
};

export function PackagesTab() {
  const t = resolveTokens(useTheme());
  const showToast = useToast();
  // DNKADM-0430: search, status filter and sort come from the URL (`packagesListView.ts`).
  const url = useListUrlState(PACKAGES_URL_DEFAULTS);
  const setUrl = url.set;
  const filter = useUrlActiveFilter(url.values.status, setUrl);
  const search = url.values.q;
  const sort = parseSort(url.values.sort, url.values.dir, PACKAGE_SORT_KEYS, PACKAGE_DEFAULT_SORT);
  const { currency } = useTenantCurrency();

  // Active/Inactive/All segmented filter (shared with Plans, §4.3): Inactive shows
  // deactivated-only, All shows both (deactivated dimmed + Reactivate).
  const packages = useResource(
    useCallback(() => listPackages(filter.status), [filter.status]),
    [filter.status],
  );

  // Create/Edit dialog: `null` = closed; `{ pkg: null }` = add; `{ pkg }` = edit.
  const [dialog, setDialog] = useState<{ pkg: Offering | null } | null>(null);

  // Confirm-delete target: `null` = no dialog; a package = confirm its soft-remove.
  const [confirmDelete, setConfirmDelete] = useState<Offering | null>(null);
  const [deleting, setDeleting] = useState(false);

  const refetch = packages.refetch;

  const handleDeactivate = useCallback(
    async (pkg: Offering) => {
      try {
        await deactivateOffering(pkg.id);
        refetch();
        showToast('Package deactivated', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t deactivate the package.', 'error');
      }
    },
    [refetch, showToast],
  );

  const handleReactivate = useCallback(
    async (pkg: Offering) => {
      try {
        await reactivateOffering(pkg.id);
        refetch();
        showToast('Package reactivated', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t reactivate the package.', 'error');
      }
    },
    [refetch, showToast],
  );

  const handlePublish = useCallback(
    async (pkg: Offering) => {
      try {
        await publishOffering(pkg.id);
        refetch();
        showToast('Package published', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t publish the package.', 'error');
      }
    },
    [refetch, showToast],
  );

  const handleCopy = useCallback(
    async (pkg: Offering) => {
      try {
        await copyOffering(pkg.id);
        refetch();
        showToast('Package duplicated', 'success');
      } catch (err) {
        showToast(serverMessage(err) ?? 'Couldn’t duplicate the package.', 'error');
      }
    },
    [refetch, showToast],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await deleteOffering(confirmDelete.id);
      setConfirmDelete(null);
      refetch();
      showToast('Package deleted', 'success');
    } catch (err) {
      showToast(serverMessage(err) ?? 'Couldn’t delete the package.', 'error');
    } finally {
      setDeleting(false);
    }
  }, [confirmDelete, refetch, showToast]);

  function priceLabel(pkg: Offering): string {
    // An UNPRICED package says so rather than rendering as `$0.00` — a real `0` is a
    // deliberately free package and still shows the zero figure (DNKADM-0245).
    if (pkg.price == null) return 'No price';
    return currency ? formatMoney(pkg.price, currency) : pkg.price.toFixed(2);
  }

  const rows = packages.data;
  const visibleRows = useMemo(
    () =>
      sortRows(
        (rows ?? []).filter((pkg) => packageMatches(pkg, search)),
        (pkg) => packageSortValue(pkg, sort.key),
        sort.dir,
        (pkg) => pkg.id,
      ),
    [rows, search, sort.key, sort.dir],
  );
  const onSort = useCallback(
    (key: PackageSortKey) => setUrl(toSortParams(toggleSort(sort, key), PACKAGE_DEFAULT_SORT)),
    [setUrl, sort],
  );
  const isFirstRun = useMemo(
    () => filter.value === 'active' && rows !== null && rows.length === 0,
    [filter.value, rows],
  );

  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          A package is a one-time purchase that grants credits once — set its name, price, and the
          credits it grants in each bucket. Packages define the catalog; selling happens elsewhere.
        </Typography>
        <Button
          variant="contained"
          startIcon={<Icon name="add" />}
          onClick={() => setDialog({ pkg: null })}
          sx={{ flexShrink: 0 }}
        >
          Add package
        </Button>
      </Stack>

      <ListToolbar
        search={{
          value: search,
          onChange: (next) => setUrl({ q: next }),
          placeholder: 'Search packages',
          disabled: packages.status !== 'ready',
        }}
        filters={
          <ActiveFilter
            value={filter.value}
            onChange={filter.setValue}
            aria-label="Filter packages by status"
          />
        }
        onClear={() => setUrl({ q: null, status: null })}
        canClear={search.length > 0 || filter.value !== 'active'}
      />

      {packages.status === 'loading' && (
        <Skeleton
          variant="rounded"
          height={260}
          role="status"
          aria-label="Loading packages"
          sx={{ borderRadius: 3 }}
        />
      )}

      {packages.status === 'error' && (
        <ScreenError
          error={packages.error}
          what="the packages"
          onRetry={packages.refetch}
          sx={{ alignItems: 'center' }}
        />
      )}

      {packages.status === 'ready' && rows && rows.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 5,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: t.lineStrong,
            borderRadius: 3,
          }}
        >
          <Icon name="inventory_2" sx={{ fontSize: 40, color: 'text.disabled' }} />
          <Typography sx={{ mt: 1, fontWeight: 600 }}>
            {isFirstRun ? 'No packages yet' : 'No packages match this filter'}
          </Typography>
          {isFirstRun && (
            <>
              <Typography variant="body2" sx={{ mt: 0.5, mb: 2.5, color: 'text.secondary' }}>
                A package is a one-time purchase that grants credits once — like a 10-visit bay
                pack. Add your first package, then set the credits it grants in each bucket.
              </Typography>
              <Button
                variant="contained"
                startIcon={<Icon name="add" />}
                onClick={() => setDialog({ pkg: null })}
              >
                Add package
              </Button>
            </>
          )}
        </Paper>
      )}

      {packages.status === 'ready' && rows && rows.length > 0 && (
        <TableContainer>
          <Table aria-label="Packages" size="small">
            <TableHead>
              <TableRow>
                <SortableHeader label="Package" col="name" sort={sort} onSort={onSort} />
                <SortableHeader
                  label="Price"
                  col="price"
                  align="right"
                  sort={sort}
                  onSort={onSort}
                />
                <SortableHeader label="Contents" col="contents" sort={sort} onSort={onSort} />
                <SortableHeader label="Status" col="status" sort={sort} onSort={onSort} />
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((pkg) => {
                const name = packageName(pkg);
                // Deactivated rows are dimmed (they only appear under Inactive / All).
                const dim = pkg.active ? undefined : { opacity: 0.6 };
                // Contents summary from the inline grants: the first grant + "+N more".
                const contents = packageContentsLabel(pkg);
                return (
                  <TableRow key={pkg.id} sx={dim}>
                    <TableCell>
                      {name}
                      {/* Who can buy (DNKADM-0412): a chip only when it's not everyone. */}
                      {pkg.purchaseAudience !== 'everyone' && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={AUDIENCE_CHIP[pkg.purchaseAudience]}
                          sx={{ ml: 1 }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">{priceLabel(pkg)}</TableCell>
                    <TableCell>
                      {contents !== null ? (
                        <Typography component="span" variant="body2">
                          {contents}
                        </Typography>
                      ) : (
                        <Tooltip title="This package grants no credits">
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{ color: 'warning.main' }}
                          >
                            No credits
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={packageStatusLabel(pkg)}
                        color={
                          pkg.status === 'DRAFT'
                            ? 'warning'
                            : pkg.status === 'INACTIVE'
                              ? 'default'
                              : 'success'
                        }
                        variant={pkg.status === 'ACTIVE' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconAction
                        size="small"
                        label={`Edit package ${name}`}
                        onClick={() => setDialog({ pkg })}
                      >
                        <Icon name="edit" />
                      </IconAction>
                      <IconAction
                        size="small"
                        label={`Duplicate package ${name}`}
                        onClick={() => void handleCopy(pkg)}
                      >
                        <Icon name="content_copy" />
                      </IconAction>
                      {pkg.status === 'DRAFT' && (
                        <IconAction
                          size="small"
                          label={`Publish package ${name}`}
                          onClick={() => void handlePublish(pkg)}
                        >
                          <Icon name="publish" />
                        </IconAction>
                      )}
                      {pkg.status === 'ACTIVE' && (
                        <IconAction
                          size="small"
                          label={`Deactivate package ${name}`}
                          onClick={() => void handleDeactivate(pkg)}
                        >
                          <Icon name="toggle_off" />
                        </IconAction>
                      )}
                      {pkg.status === 'INACTIVE' && (
                        <IconAction
                          size="small"
                          label={`Reactivate package ${name}`}
                          onClick={() => void handleReactivate(pkg)}
                        >
                          <Icon name="toggle_on" />
                        </IconAction>
                      )}
                      <IconAction
                        size="small"
                        label={`Delete package ${name}`}
                        onClick={() => setConfirmDelete(pkg)}
                      >
                        <Icon name="delete" />
                      </IconAction>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {packages.status === 'ready' && rows && rows.length > 0 && visibleRows.length === 0 && (
        <ListNoMatch noun="packages" query={search} />
      )}

      <Dialog
        open={confirmDelete !== null}
        onClose={() => (deleting ? undefined : setConfirmDelete(null))}
        aria-labelledby="delete-package-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="delete-package-title">Delete package?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14 }}>
            {confirmDelete
              ? `“${packageName(confirmDelete)}” will be removed from the catalog. This can’t be undone.`
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} color="inherit" disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={() => void handleConfirmDelete()} disabled={deleting}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <PackageDialog
        open={dialog !== null}
        pkg={dialog?.pkg ?? null}
        currency={currency}
        onClose={() => setDialog(null)}
        onSaved={() => {
          setDialog(null);
          refetch();
        }}
        showToast={showToast}
      />
    </Box>
  );
}
